import React, { useEffect, useState } from 'react'
import { Loader2, Info, DollarSign, Radio, History, ChevronUp, ChevronDown } from 'lucide-react'
import Drawer from '../ui/Drawer'
import Tooltip from '../ui/Tooltip'
import { useToast } from '../../contexts/ToastContext'
import broadcastAdminService, {
  Broadcast, RefViewershipSource, BroadcastChannel, ChannelOptions,
  RateCard, ValuationLogEntry
} from '../../services/broadcastAdminService'

/**
 * Column names, as a person would say them. The journal stores the raw column
 * so it never needs a migration to log a new field; this is where it becomes
 * readable, and an unmapped field falls back to its column name rather than
 * disappearing.
 */
const FIELD_LABELS: Record<string, string> = {
  rate_card_id: 'Rate card',
  cpm: 'CPM override',
  currency: 'Currency',
  viewers: 'Audience',
  viewership_source_id: 'Viewership source',
  channel_id: 'Feed Provider',
  feed_role: 'Feed role'
}

interface Props {
  isOpen: boolean
  broadcast: Broadcast | null
  customerId: string
  onClose: () => void
  onSaved: () => void
}

const FEED_ROLES = [
  { value: 'primary',      label: 'Primary — the event itself',
    hint: 'A standalone broadcast, or the parent of a multi-feed event.' },
  { value: 'continuation', label: 'Continuation — same audience, another file',
    hint: 'A rain-split game. Exposure sums into the parent and is priced at the parent’s rate.' },
  { value: 'parallel',     label: 'Parallel — a different feed with its own audience',
    hint: 'Prelims against main card, broadcaster against streaming platform. Priced at its OWN audience; the values are summed, never the exposure.' },
  { value: 'cross_check',  label: 'Cross-check — measured, never counted',
    hint: 'A second recording of the same event, used for an error bar. Contributes to no total.' }
]

/**
 * What support needs to turn exposure into money.
 *
 * SEPARATE FROM UPLOAD, AND THAT IS THE POINT. None of this is knowable when
 * a file is uploaded. Audience arrives days or weeks after an event — Nielsen,
 * a platform report, a rights-holder estimate — so a broadcast is normally
 * analysed long before it can be priced. An upload form that demanded these
 * would be a form nobody could complete.
 *
 * NOTHING HERE TRIGGERS A RECOMPUTE, by design. Value is derived at read:
 * rate is audience x CPM in the SELECT, and value is area-seconds / 30 x rate
 * in the client. Save an audience three weeks late and every figure is correct
 * on the next page load, with no job to run and no stale rows. The only thing
 * that does not move is a published report, which is frozen HTML on purpose —
 * a sponsor must keep seeing the number they were sent.
 */
const BroadcastMeasurementDrawer: React.FC<Props> = ({
  isOpen, broadcast, customerId, onClose, onSaved
}) => {
  const { showError, showSuccess } = useToast()
  const [sources, setSources] = useState<RefViewershipSource[]>([])
  const [channels, setChannels] = useState<ChannelOptions>({ mine: [], all: [] })
  // Opens the full catalogue. The short list is the feature, but see below:
  // a customer with no list yet is shown everything, because an empty picker
  // with no visible way forward is worse than a long one.
  const [allChannels, setAllChannels] = useState(false)
  // A failed fetch must not look like "there are no channels". Without this
  // an unapplied migration, an expired session and a genuinely empty
  // catalogue are the same empty dropdown.
  const [channelsFailed, setChannelsFailed] = useState(false)
  const [saving, setSaving] = useState(false)

  const [viewers, setViewers] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [cpm, setCpm] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [channelId, setChannelId] = useState('')
  const [feedRole, setFeedRole] = useState('primary')
  const [rateCardId, setRateCardId] = useState('')
  const [rateCards, setRateCards] = useState<RateCard[]>([])
  const [history, setHistory] = useState<ValuationLogEntry[]>([])
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    broadcastAdminService.listReference('viewership-sources')
      .then((r) => setSources((r.data as RefViewershipSource[]).filter((s) => s.is_active)))
      .catch(() => setSources([]))
    setChannelsFailed(false)
    broadcastAdminService.listChannels(customerId)
      .then((r) => {
        setChannels(r.data)
        // Nothing enabled yet is the NORMAL state for a customer who has not
        // saved a broadcast against a channel — which is every customer, the
        // first time. Showing them an empty list and a "show all" link they
        // have to find is a worse default than simply showing the catalogue.
        if (r.data.mine.length === 0) setAllChannels(true)
      })
      .catch(() => { setChannels({ mine: [], all: [] }); setChannelsFailed(true) })

    // enabledOnly: this is the PICKER. Cards someone deliberately stopped
    // offering must not reappear here, even though they keep pricing the
    // broadcasts already assigned to them.
    broadcastAdminService.listRateCards(customerId, true)
      .then((r) => setRateCards(r.data || []))
      .catch(() => setRateCards([]))
  }, [isOpen, customerId])

  // The trail, loaded per broadcast rather than on open: the drawer is reused
  // across rows and showing the previous broadcast's history would be worse
  // than showing none.
  useEffect(() => {
    if (!isOpen || !broadcast) { setHistory([]); return }
    broadcastAdminService.getValuationLog(customerId, broadcast.id)
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
  }, [isOpen, broadcast, customerId])

  // Repopulate whenever the subject changes, not only on open: the drawer is
  // reused across rows and a stale form would silently save one broadcast's
  // numbers onto another.
  useEffect(() => {
    const b = broadcast
    setViewers(b?.viewers != null ? String(b.viewers) : '')
    setSourceId(b?.viewership_source_id ?? '')
    setCpm(b?.cpm != null ? String(b.cpm) : '')
    setCurrency(b?.currency?.trim() || 'USD')
    setChannelId(b?.channel_id ?? '')
    setFeedRole(b?.feed_role ?? 'primary')
    setRateCardId(b?.rate_card_id ?? '')
    setAllChannels(false)
    setShowHistory(false)
  }, [broadcast])

  if (!broadcast) return null

  const nViewers = viewers.trim() === '' ? null : Number(viewers)
  const nCpm = cpm.trim() === '' ? null : Number(cpm)

  /**
   * A BLANK CPM NO LONGER MEANS UNPRICED. It means "inherit the rate card",
   * which is now the ordinary case and the entire point of the cards. Before
   * this, leaving the field empty showed "Not priced" even when a card
   * covered the broadcast perfectly well, which would have taught people to
   * type the rate in by hand - the habit the cards exist to break.
   *
   * The server resolves this the same way and is the authority; this is the
   * preview, so it has to use the same precedence or saving would change the
   * number on screen.
   */
  // The card currently SELECTED in the form, not the one last saved — the
  // preview has to follow the picker or choosing a card would appear to do
  // nothing until save.
  const pickedCard = rateCards.find((c) => c.id === rateCardId)
    ?? (broadcast.rate_card_id === rateCardId && broadcast.rate_card_cpm != null
          ? { id: broadcast.rate_card_id, name: broadcast.rate_card_name ?? 'Assigned card',
              cpm: Number(broadcast.rate_card_cpm) } as Pick<RateCard, 'id' | 'name' | 'cpm'>
          : undefined)
  const inheritedCpm = pickedCard ? Number(pickedCard.cpm) : null
  const effectiveCpm = nCpm !== null && Number.isFinite(nCpm) ? nCpm : inheritedCpm
  const inheriting = (nCpm === null || !Number.isFinite(nCpm)) && inheritedCpm !== null

  const rate = nViewers !== null && Number.isFinite(nViewers) && effectiveCpm !== null
    ? (nViewers / 1000) * effectiveCpm
    : null

  const chosenSource = sources.find((s) => s.id === sourceId)

  // The saved channel must always be selectable, even when it is not on this
  // customer's list — otherwise opening the drawer on an older broadcast
  // silently blanks a value the user never touched.
  const base: BroadcastChannel[] = allChannels ? channels.all : channels.mine
  const channelList = channelId && !base.some((c) => c.id === channelId)
    ? [...channels.all.filter((c) => c.id === channelId), ...base]
    : base

  const save = async () => {
    if (viewers.trim() !== '' && (!Number.isFinite(nViewers!) || nViewers! < 0)) {
      showError('Check the audience', 'Viewers must be a whole number of people, or blank.')
      return
    }
    if (cpm.trim() !== '' && (!Number.isFinite(nCpm!) || nCpm! < 0)) {
      showError('Check the CPM', 'CPM must be a positive amount, or blank.')
      return
    }
    // The database refuses this pairing; catching it here gives a sentence
    // instead of a constraint name.
    const isChild = !!broadcast.parent_broadcast_id
    if (isChild && feedRole === 'primary') {
      showError('Pick a feed role', 'This broadcast belongs to a parent, so it cannot be the primary feed.')
      return
    }
    if (!isChild && feedRole !== 'primary') {
      showError('Pick a feed role', 'This broadcast has no parent, so it is the primary feed.')
      return
    }

    setSaving(true)
    try {
      await broadcastAdminService.updateBroadcast(customerId, broadcast.id, {
        // null, never 0 — clearing a field must mean "not known", and a zero
        // audience would price the whole broadcast at nothing without
        // anything failing.
        viewers: nViewers,
        viewershipSourceId: sourceId || null,
        cpm: nCpm,
        currency: currency.trim().toUpperCase() || null,
        channelId: channelId || null,
        feedRole,
        rateCardId: rateCardId || null
      })
      showSuccess('Saved', rate === null
        ? 'Saved. Exposure is not priced until there is both an audience and a CPM.'
        : `Saved. This broadcast prices at ${rate.toLocaleString(undefined, {
            style: 'currency', currency: currency.trim().toUpperCase() || 'USD',
            maximumFractionDigits: 0
          })} per :30.`)
      onSaved()
      onClose()
    } catch (err) {
      showError('Could not save', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setSaving(false)
    }
  }

  const field = 'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white'
  const label = 'block text-xs font-medium text-gray-700 mb-1'

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Valuation inputs">
      <div className="space-y-5">
        <p className="text-xs text-gray-500 leading-snug">
          {broadcast.title}
        </p>

        <section className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <DollarSign className="h-3.5 w-3.5" /> Valuation
          </h4>

          <div>
            <label className={label} htmlFor="bm-viewers">Audience</label>
            <input id="bm-viewers" className={field} inputMode="numeric" value={viewers}
                   onChange={(e) => setViewers(e.target.value)} placeholder="e.g. 25000" />
          </div>

          <div>
            <label className={label} htmlFor="bm-source">Where the audience figure came from</label>
            <select id="bm-source" className={field} value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}>
              <option value="">Not recorded</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.is_estimate ? ' (estimate)' : ''}
                </option>
              ))}
            </select>
            {chosenSource && (
              <p className="text-[11px] text-gray-500 mt-1">
                {chosenSource.is_estimate
                  ? 'An estimate. Figures from it should be presented as provisional.'
                  : 'A measured source. Figures from it can be presented as confirmed.'}
              </p>
            )}
          </div>

          {/* THE CARD IS THE PRIMARY CONTROL, so it sits above the override
              it is overridden by. Nothing resolves a card automatically:
              until one is picked (or a CPM typed), the broadcast is worth
              nothing, which is the honest default. */}
          <div>
            <label className={label} htmlFor="bm-card">Rate card</label>
            <select id="bm-card" className={field} value={rateCardId}
                    onChange={(e) => setRateCardId(e.target.value)}>
              <option value="">No rate card — not priced</option>
              {rateCards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {Number(c.cpm).toFixed(2)} {c.currency}
                  {c.channel_name ? ` · ${c.channel_name}` : ''}
                </option>
              ))}
            </select>
            {rateCards.length === 0 && (
              <p className="text-[11px] text-gray-400 mt-1">
                No cards offered yet. Create one on the Rate cards tab, or type a CPM below
                for a one-off.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className={label} htmlFor="bm-cpm">
                CPM <span className="font-normal text-gray-400">— override</span>
              </label>
              <input id="bm-cpm" className={field} inputMode="decimal" value={cpm}
                     onChange={(e) => setCpm(e.target.value)}
                     placeholder={inheritedCpm !== null
                       ? `Card says ${inheritedCpm.toFixed(2)} — leave blank to use it`
                       : 'No card selected'} />
            </div>
            <div>
              <label className={label} htmlFor="bm-currency">Currency</label>
              <input id="bm-currency" className={field} maxLength={3} value={currency}
                     onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
            </div>
          </div>

          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
            <p className="text-[11px] text-gray-500">Resulting 30-second rate</p>
            <p className="text-sm font-semibold text-gray-900 tabular-nums">
              {rate === null
                ? effectiveCpm === null
                  ? 'Not priced — no rate card covers this, and no override is set'
                  : 'Not priced — needs an audience'
                : `${rate.toLocaleString(undefined, {
                    style: 'currency', currency: currency.trim().toUpperCase() || 'USD',
                    maximumFractionDigits: 2
                  })} per :30`}
            </p>
            {/* WHICH METHOD PRICED IT, stated on the screen that sets it.
                This is the same distinction a report has to declare, so the
                person entering the numbers sees the sentence their sponsor
                will eventually read. */}
            {effectiveCpm !== null && (
              <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                {inheriting
                  ? <>Rate card{pickedCard
                        ? <> <span className="font-medium text-gray-700">{pickedCard.name}</span></>
                        : ''} — {inheritedCpm!.toFixed(2)} CPM. Declared as “Customer rate card”.</>
                  : <>Negotiated rate for this broadcast, overriding{' '}
                      {inheritedCpm !== null
                        ? `the card's ${inheritedCpm.toFixed(2)}`
                        : 'no card'}. Declared as “Negotiated rate”.</>}
              </p>
            )}
          </div>
        </section>

        {/* The trail. Collapsed by default — it is the answer to a question
            nobody asks until a sponsor disputes a figure, and expanding it by
            default would push the controls off the screen. */}
        {history.length > 0 && (
          <section className="pt-1 border-t border-gray-100">
            <button type="button" onClick={() => setShowHistory((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider pt-4 hover:text-gray-700">
              <History className="h-3.5 w-3.5" />
              Change history ({history.length})
              {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showHistory && (
              <ol className="mt-2 space-y-1.5">
                {history.map((h) => (
                  <li key={h.id} className="text-[11px] text-gray-600 leading-snug">
                    <span className="font-medium text-gray-800">{FIELD_LABELS[h.field] ?? h.field}</span>
                    {' '}
                    {h.old_value === null
                      ? <>set to <b>{h.new_label ?? h.new_value}</b></>
                      : h.new_value === null
                        ? <>cleared (was <b>{h.old_label ?? h.old_value}</b>)</>
                        : <><b>{h.old_label ?? h.old_value}</b> → <b>{h.new_label ?? h.new_value}</b></>}
                    <span className="text-gray-400">
                      {' · '}{h.actor_name ?? 'unknown'}
                      {' · '}{new Date(h.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}

        <section className="space-y-3 pt-1 border-t border-gray-100">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 pt-4">
            <Radio className="h-3.5 w-3.5" /> Feed
          </h4>

          <div>
            <label className={label} htmlFor="bm-channel">Feed Provider</label>
            <select id="bm-channel" className={field} value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}>
              <option value="">Select feed provider</option>
              {channelList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.territory ? ` \u00b7 ${c.territory}` : ''}
                </option>
              ))}
            </select>
            <div className="flex items-center justify-between mt-1 gap-2">
              <p className="text-[11px] text-gray-500">
                The network or platform that carried this feed to its audience.
              </p>
              {/* The catalogue is reachable but not the default, because one
                  list of every distributor is what this design avoids. Picking
                  from it adds the channel to this customer on save. */}
              {!allChannels && channels.all.length > channels.mine.length && (
                <button type="button" onClick={() => setAllChannels(true)}
                        className="text-[11px] font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
                  Show all
                </button>
              )}
              {allChannels && channels.mine.length > 0 && (
                <button type="button" onClick={() => setAllChannels(false)}
                        className="text-[11px] font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
                  Show theirs
                </button>
              )}
            </div>
            {channelsFailed && (
              <p className="text-[11px] text-red-600 mt-1">
                Could not load channels. This is not an empty list — the request failed.
              </p>
            )}
            {!channelsFailed && allChannels && (
              <p className="text-[11px] text-gray-400 mt-1">
                {channels.mine.length === 0
                  ? 'This customer has no channels yet. Picking one adds it to their list.'
                  : 'Showing every channel. The one you pick is added to their list.'}
              </p>
            )}
          </div>

          <div>
            <label className={label} htmlFor="bm-role">
              Feed role
              <Tooltip
                maxWidth={280}
                content="Decides how this feed's exposure is counted against its parent. Changing it changes the numbers."
              >
                <Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" />
              </Tooltip>
            </label>
            <select id="bm-role" className={field} value={feedRole}
                    onChange={(e) => setFeedRole(e.target.value)}>
              {FEED_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 mt-1 leading-snug">
              {FEED_ROLES.find((r) => r.value === feedRole)?.hint}
            </p>
          </div>
        </section>

        <div className="flex gap-2 pt-2">
          <button onClick={() => void save()} disabled={saving}
                  className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50">
            {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : 'Save'}
          </button>
          <button onClick={onClose} disabled={saving}
                  className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </Drawer>
  )
}

export default BroadcastMeasurementDrawer
